import { ENEMY_KINDS, type EnemyKind } from '../data/enemies'
import { TOWER_KINDS, type TowerKind } from '../data/towers'
import { MAP_IDS, type MapId } from '../data/maps'
import { RUN_MODE_IDS, type RunMode } from '../data/endgame'
import type { PerformanceRank, RunResult } from '../progression/MetaProgress'
import { balanceExperiment, type BalanceTrack } from '../balance/BalanceExperiment'

const STORAGE_KEY = 'block-defense.playtests.v1'
const HISTORY_LIMIT = 30

export interface PlaytestRun {
  readonly id: string
  readonly completedAt: string
  readonly version: string
  readonly balanceTrack: BalanceTrack
  readonly mapId: MapId
  readonly runMode: RunMode
  readonly victory: boolean
  readonly wave: number
  readonly coreIntegrity: number
  readonly score: number
  readonly rank: PerformanceRank
  readonly kills: number
  readonly towerDamage: Record<TowerKind, number>
  readonly breaches: Record<EnemyKind, number>
  readonly towersBuilt: number
  readonly towersUpgraded: number
}

export interface PlaytestSummary {
  readonly runs: number
  readonly wins: number
  readonly winRate: number
  readonly averageScore: number
  readonly averageCore: number
  readonly averageWave: number
  readonly towerDamage: Record<TowerKind, number>
  readonly towerShare: Record<TowerKind, number>
  readonly breaches: Record<EnemyKind, number>
  readonly dominantTower: TowerKind | null
  readonly breachLeader: EnemyKind | null
  readonly signals: readonly string[]
  readonly recent: readonly PlaytestRun[]
  readonly trackStats: Record<BalanceTrack, { readonly runs: number; readonly wins: number; readonly winRate: number; readonly averageScore: number }>
}

class PlaytestHistory {
  private runs = this.load()

  get summary(): PlaytestSummary { return summarize(this.runs) }

  record(result: RunResult): PlaytestSummary {
    const run: PlaytestRun = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      completedAt: new Date().toISOString(),
      version: __APP_VERSION__,
      balanceTrack: balanceExperiment.active,
      mapId: result.mapId,
      runMode: result.runMode,
      victory: result.victory,
      wave: result.wave,
      coreIntegrity: result.coreIntegrity,
      score: result.score,
      rank: result.rank,
      kills: result.runKills,
      towerDamage: { ...result.telemetry.towerDamage },
      breaches: { ...result.telemetry.breaches },
      towersBuilt: result.telemetry.towersBuilt,
      towersUpgraded: result.telemetry.towersUpgraded,
    }
    this.runs = [run, ...this.runs].slice(0, HISTORY_LIMIT)
    this.save()
    return this.summary
  }

  exportHistory(): string {
    return JSON.stringify({ version: 1, exportedAt: new Date().toISOString(), runs: this.runs }, null, 2)
  }

  private load(): PlaytestRun[] {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]') as unknown
      if (!Array.isArray(parsed)) return []
      return parsed.filter(isPlaytestRun).slice(0, HISTORY_LIMIT)
    } catch {
      return []
    }
  }

  private save(): void {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.runs)) } catch { /* storage can be unavailable */ }
  }
}

function summarize(runs: readonly PlaytestRun[]): PlaytestSummary {
  const wins = runs.filter((run) => run.victory).length
  const towerDamage = { SOL: 0, ETH: 0, BTC: 0 }
  const breaches = { BOT: 0, PAPER_HAND: 0, FUD_BOT: 0, LIQUIDATOR: 0, WHALE: 0 }
  runs.forEach((run) => {
    TOWER_KINDS.forEach((kind) => { towerDamage[kind] += run.towerDamage[kind] })
    ENEMY_KINDS.forEach((kind) => { breaches[kind] += run.breaches[kind] })
  })
  const totalDamage = TOWER_KINDS.reduce((sum, kind) => sum + towerDamage[kind], 0)
  const towerShare = Object.fromEntries(TOWER_KINDS.map((kind) => [kind, totalDamage > 0 ? towerDamage[kind] / totalDamage : 0])) as Record<TowerKind, number>
  const dominantTower = totalDamage > 0 ? [...TOWER_KINDS].sort((a, b) => towerDamage[b] - towerDamage[a])[0] : null
  const totalBreaches = ENEMY_KINDS.reduce((sum, kind) => sum + breaches[kind], 0)
  const breachLeader = totalBreaches > 0 ? [...ENEMY_KINDS].sort((a, b) => breaches[b] - breaches[a])[0] : null
  const winRate = runs.length > 0 ? wins / runs.length : 0
  const signals: string[] = []
  if (runs.length < 5) signals.push(`COLLECT ${5 - runs.length} MORE RUN${5 - runs.length === 1 ? '' : 'S'} FOR A BASELINE`)
  else {
    if (winRate >= 0.8) signals.push('WIN RATE ABOVE 80% // REVIEW THREAT PRESSURE')
    if (winRate <= 0.35) signals.push('WIN RATE BELOW 35% // REVIEW EARLY ECONOMY')
    TOWER_KINDS.forEach((kind) => {
      if (towerShare[kind] >= 0.55) signals.push(`${kind} EXCEEDS 55% DAMAGE SHARE`)
      if (towerShare[kind] <= 0.08 && totalDamage > 0) signals.push(`${kind} BELOW 8% DAMAGE SHARE`)
    })
    if (signals.length === 0) signals.push('NO CRITICAL BALANCE OUTLIER DETECTED')
  }
  const average = (selector: (run: PlaytestRun) => number) => runs.length > 0 ? runs.reduce((sum, run) => sum + selector(run), 0) / runs.length : 0
  const trackStats = Object.fromEntries((['CONTROL', 'ARSENAL_PARITY'] as const).map((track) => {
    const samples = runs.filter((run) => run.balanceTrack === track)
    const trackWins = samples.filter((run) => run.victory).length
    return [track, { runs: samples.length, wins: trackWins, winRate: samples.length > 0 ? trackWins / samples.length : 0, averageScore: samples.length > 0 ? samples.reduce((sum, run) => sum + run.score, 0) / samples.length : 0 }]
  })) as PlaytestSummary['trackStats']
  return { runs: runs.length, wins, winRate, averageScore: average((run) => run.score), averageCore: average((run) => run.coreIntegrity), averageWave: average((run) => run.wave), towerDamage, towerShare, breaches, dominantTower, breachLeader, signals, recent: runs.slice(0, 8), trackStats }
}

function isPlaytestRun(value: unknown): value is PlaytestRun {
  if (!value || typeof value !== 'object') return false
  const run = value as Partial<PlaytestRun>
  const validRank = run.rank === 'S' || run.rank === 'A' || run.rank === 'B' || run.rank === 'C' || run.rank === 'D'
  const validTowerDamage = Boolean(run.towerDamage) && TOWER_KINDS.every((kind) => Number.isFinite(run.towerDamage?.[kind]) && (run.towerDamage?.[kind] ?? -1) >= 0)
  const validBreaches = Boolean(run.breaches) && ENEMY_KINDS.every((kind) => Number.isFinite(run.breaches?.[kind]) && (run.breaches?.[kind] ?? -1) >= 0)
  return typeof run.id === 'string' && typeof run.completedAt === 'string' && typeof run.version === 'string' && (run.balanceTrack === 'CONTROL' || run.balanceTrack === 'ARSENAL_PARITY') && typeof run.victory === 'boolean'
    && typeof run.wave === 'number' && Number.isFinite(run.wave) && run.wave >= 1 && typeof run.score === 'number' && Number.isFinite(run.score) && run.score >= 0
    && typeof run.coreIntegrity === 'number' && Number.isFinite(run.coreIntegrity) && run.coreIntegrity >= 0 && run.coreIntegrity <= 100
    && typeof run.kills === 'number' && Number.isFinite(run.kills) && run.kills >= 0 && MAP_IDS.includes(run.mapId as MapId) && RUN_MODE_IDS.includes(run.runMode as RunMode)
    && validRank && validTowerDamage && validBreaches
}

export const playtestHistory = new PlaytestHistory()
